/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get({
    inboxId,
    status,
    assigneeType,
    assigneeId,
    page,
    labels,
    teamId,
    conversationType,
    sortBy,
    updatedWithin,
    // NOTICE: KAPTION-INTEGRATION - Add assistantId parameter for filtering
    assistantId,
    // NOTICE: KAPTION-INTEGRATION - ai-takeover.LIST.2 ("Quién atiende")
    kaptionHandler,
  }) {
    return axios.get(this.url, {
      params: {
        inbox_id: inboxId,
        team_id: teamId,
        status,
        assignee_type: assigneeType,
        assignee_id: assigneeId,
        page,
        labels,
        conversation_type: conversationType,
        sort_by: sortBy,
        updated_within: updatedWithin,
        // NOTICE: KAPTION-INTEGRATION - Pass assistant_id to API
        assistant_id: assistantId,
        kaption_handler: kaptionHandler,
      },
    });
  }

  filter(payload) {
    return axios.post(`${this.url}/filter`, payload.queryData, {
      params: {
        page: payload.page,
      },
    });
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  toggleStatus({ conversationId, status, snoozedUntil = null }) {
    return axios.post(`${this.url}/${conversationId}/toggle_status`, {
      status,
      snoozed_until: snoozedUntil,
    });
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId }) {
    return axios.post(
      `${this.url}/${conversationId}/assignments?assignee_id=${agentId}`,
      {}
    );
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  // NOTICE: KAPTION-INTEGRATION - Bulk mark conversations as read for the current user.
  // Accepts the same scoping params as the index endpoint so callers can mark-all-read
  // inside a specific view (e.g. the Unread filter tab).
  markAllRead({
    inboxId,
    status,
    assigneeType,
    conversationType,
    assistantId,
    // NOTICE: KAPTION-INTEGRATION - ai-takeover.LIST.4 (ChatList passes snake_case)
    kaptionHandler,
    kaption_handler: kaptionHandlerSnake,
  } = {}) {
    return axios.post(`${this.url}/mark_all_read`, {
      kaption_handler: kaptionHandler || kaptionHandlerSnake,
      inbox_id: inboxId,
      status,
      assignee_type: assigneeType,
      conversation_type: conversationType,
      assistant_id: assistantId,
    });
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  mute(conversationId, mutedUntil = null) {
    return axios.post(`${this.url}/${conversationId}/mute`, {
      muted_until: mutedUntil,
    });
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  pin(conversationId) {
    return axios.post(`${this.url}/${conversationId}/pin`);
  }

  unpin(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unpin`);
  }

  meta({
    inboxId,
    status,
    assigneeType,
    assigneeId,
    labels,
    teamId,
    conversationType,
    assistantId,
    // NOTICE: KAPTION-INTEGRATION - ai-takeover.LIST.2
    kaptionHandler,
  }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        assignee_id: assigneeId,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
        // NOTICE: KAPTION-INTEGRATION - Pass assistant_id to API meta endpoint
        assistant_id: assistantId,
        kaption_handler: kaptionHandler,
      },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  updateAdditionalAttributes({ conversationId, additionalAttributes }) {
    return axios.post(`${this.url}/${conversationId}/additional_attributes`, {
      additional_attributes: additionalAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  getInboxAssistant(conversationId) {
    return axios.get(`${this.url}/${conversationId}/inbox_assistant`);
  }

  delete(conversationId) {
    return axios.delete(`${this.url}/${conversationId}`);
  }

  // NOTICE: KAPTION-INTEGRATION
  // Live translation for reply box
  translateLive({ conversationId, text, sourceLanguage, targetLanguage }) {
    return axios.post(
      `${this.url}/${conversationId}/kaption_live_translations`,
      {
        text,
        source_language: sourceLanguage,
        target_language: targetLanguage,
      }
    );
  }

  // NOTICE: KAPTION-INTEGRATION
  // Batch translation for automatic translation triggering
  translateBatch(conversationId, messageIds, targetLanguage) {
    return axios.post(`${this.url}/${conversationId}/translate_batch`, {
      message_ids: messageIds,
      target_language: targetLanguage,
    });
  }

  // Group Contacts API methods
  fetchGroupContacts(conversationId, page = 1) {
    return axios.get(`${this.url}/${conversationId}/group_contacts`, {
      params: { page },
    });
  }

  addGroupContacts(conversationId, contactIds) {
    return axios.post(`${this.url}/${conversationId}/group_contacts`, {
      contact_ids: contactIds,
    });
  }

  updateGroupContacts(conversationId, contactIds) {
    return axios.put(`${this.url}/${conversationId}/group_contacts`, {
      contact_ids: contactIds,
    });
  }

  removeGroupContacts(conversationId, contactIds) {
    return axios.delete(`${this.url}/${conversationId}/group_contacts`, {
      data: { contact_ids: contactIds },
    });
  }
}

export default new ConversationApi();
